import { type FC, useState, useEffect } from 'react';
import clsx from 'clsx';
import {
  SEMI_FABS, SEMI_EQUIPMENT, SEMI_MATERIALS, SEMI_CHOKES, getSemiRiskScore,
  type SemiFab, type SemiEquipment, type SemiMaterial, type SemiChoke,
} from '@/services/semiconductor';
import { useAppStore } from '@/stores/app-store';
import type { NewsItem } from '@/types';

// ---------------------------------------------------------------------------
// Semiconductor Supply Chain Intelligence Panel
// Fab locations, equipment chokepoints, critical materials, geopolitical risks
// ---------------------------------------------------------------------------

const RISK_COLORS = {
  critical: { bg: 'bg-red-950/60', text: 'text-red-300', border: 'border-red-800/40', badge: 'bg-red-900/60 text-red-300' },
  high:     { bg: 'bg-orange-950/40', text: 'text-orange-300', border: 'border-orange-800/40', badge: 'bg-orange-900/60 text-orange-300' },
  medium:   { bg: 'bg-yellow-950/30', text: 'text-yellow-300', border: 'border-yellow-800/30', badge: 'bg-yellow-900/60 text-yellow-300' },
  low:      { bg: 'bg-emerald-950/20', text: 'text-emerald-300', border: 'border-emerald-800/20', badge: 'bg-emerald-900/60 text-emerald-300' },
};

const FLAG: Record<string, string> = {
  TW: '🇹🇼', KR: '🇰🇷', US: '🇺🇸', CN: '🇨🇳', NL: '🇳🇱', JP: '🇯🇵', GB: '🇬🇧', UA: '🇺🇦', RU: '🇷🇺',
};

type Tab = 'chokes' | 'fabs' | 'equipment' | 'materials' | 'news';

const SEMI_KEYWORDS = [
  'tsmc', 'asml', 'semiconductor', 'chip', 'foundry', 'wafer', 'euv', 'lithography',
  'nvidia', 'intel', 'samsung foundry', 'sk hynix', 'globalfoundries', 'arm holdings',
  'gallium', 'germanium', 'photoresist', 'export control', 'chip ban', 'fab',
  'chipmaker', 'microchip', 'silicon', 'advanced chip', 'huawei chip',
  'lam research', 'applied materials', 'kla corporation', 'synopsys', 'cadence',
];

function filterSemiNews(items: NewsItem[]): NewsItem[] {
  return items.filter((n) => {
    const text = `${n.title} ${n.description ?? ''}`.toLowerCase();
    return SEMI_KEYWORDS.some((kw) => text.includes(kw));
  }).slice(0, 30);
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const NewsRow: FC<{ item: NewsItem }> = ({ item }) => (
  <a
    href={item.url}
    target="_blank"
    rel="noopener noreferrer"
    className="block rounded p-2 border border-slate-800/40 mb-1 hover:bg-slate-800/30 transition-colors"
  >
    <div className="flex items-start gap-1.5">
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold text-slate-200 leading-snug line-clamp-2">{item.title}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[9px] text-cyan-500 font-medium">{item.source}</span>
          <span className="text-[9px] text-slate-500">{timeAgo(item.publishedAt)}</span>
        </div>
      </div>
      <span className="shrink-0 text-[9px] text-slate-600">↗</span>
    </div>
  </a>
);

const FabRow: FC<{ fab: SemiFab }> = ({ fab }) => {
  const rc = RISK_COLORS[fab.riskLevel];
  return (
    <div className={clsx('rounded p-2 border mb-1', rc.bg, rc.border)}>
      <div className="flex items-start gap-1.5">
        <span className="text-sm shrink-0">{FLAG[fab.countryCode] ?? '🏭'}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-bold text-slate-100">{fab.company}</span>
            <span className="text-[9px] text-slate-400">{fab.city}</span>
            <span className={clsx('text-[8px] px-1 rounded font-bold', rc.badge)}>{fab.riskLevel.toUpperCase()}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[9px] text-cyan-400 font-mono">{fab.node}</span>
            <span className="text-[9px] text-slate-500">{fab.globalSharePct}% global share</span>
          </div>
          <p className="text-[9px] text-slate-400 mt-0.5 leading-snug">{fab.riskReason}</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {fab.customers.slice(0, 4).map((c) => (
              <span key={c} className="text-[8px] px-1 py-0.5 rounded bg-slate-800/60 text-slate-400">{c}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const EquipRow: FC<{ eq: SemiEquipment }> = ({ eq }) => {
  const rc = RISK_COLORS[eq.riskLevel];
  return (
    <div className={clsx('rounded p-2 border mb-1', rc.bg, rc.border)}>
      <div className="flex items-center gap-1.5">
        <span className="text-sm">{FLAG[eq.countryCode] ?? '🔧'}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-bold text-slate-100">{eq.company}</span>
            <span className={clsx('text-[8px] px-1 rounded font-bold', rc.badge)}>{eq.monopolyPct}% market</span>
          </div>
          <p className="text-[9px] text-slate-300 font-medium">{eq.product}</p>
          <p className="text-[9px] text-slate-400 mt-0.5 leading-snug">{eq.note}</p>
        </div>
      </div>
    </div>
  );
};

const MaterialRow: FC<{ mat: SemiMaterial }> = ({ mat }) => {
  const rc = RISK_COLORS[mat.riskLevel];
  return (
    <div className={clsx('rounded p-2 border mb-1', rc.bg, rc.border)}>
      <div className="flex items-center gap-1.5">
        <span className="text-sm">{FLAG[mat.countryCode] ?? '⚗️'}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-semibold text-slate-100">{mat.material}</span>
            <span className={clsx('text-[8px] px-1 rounded font-bold', rc.badge)}>{mat.globalSharePct}% supply</span>
          </div>
          <p className="text-[9px] text-slate-400">{mat.topSupplier} · {mat.supplierCountry}</p>
          <p className="text-[9px] text-slate-400 mt-0.5 leading-snug">{mat.note}</p>
        </div>
      </div>
    </div>
  );
};

const ChokeRow: FC<{ c: SemiChoke }> = ({ c }) => {
  const rc = RISK_COLORS[c.severity];
  return (
    <div className={clsx('rounded p-2 border mb-1', rc.bg, rc.border)}>
      <div className="flex items-start gap-1.5">
        <span className={clsx('text-[8px] font-black px-1.5 py-1 rounded shrink-0 mt-0.5 uppercase tracking-wider', rc.badge)}>
          {c.severity}
        </span>
        <div className="flex-1 min-w-0">
          <p className={clsx('text-[10px] font-bold leading-tight', rc.text)}>{c.title}</p>
          <p className="text-[9px] text-slate-400 mt-0.5 leading-snug">{c.description}</p>
          <div className="mt-1 rounded bg-slate-800/40 px-1.5 py-1">
            <span className="text-[8px] font-semibold text-amber-400">⚠ Impact: </span>
            <span className="text-[9px] text-slate-300">{c.impact}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SemiconductorPanel: FC = () => {
  const [tab, setTab] = useState<Tab>('chokes');
  const [, setTick] = useState(0);
  const riskScore = getSemiRiskScore();

  const allNews = useAppStore((s) => s.news);
  const asiaNews = useAppStore((s) => s.asiaNews);
  const semiNews = filterSemiNews([...allNews, ...asiaNews]);

  // Refresh time-ago labels every 60s
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: 'chokes',    label: 'Risk',      count: SEMI_CHOKES.length },
    { id: 'fabs',      label: 'Fabs',      count: SEMI_FABS.length },
    { id: 'equipment', label: 'Equipment', count: SEMI_EQUIPMENT.length },
    { id: 'materials', label: 'Materials', count: SEMI_MATERIALS.length },
    { id: 'news',      label: 'Live',      count: semiNews.length },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-400">💾 Chip Supply Chain</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] text-slate-500">Supply Risk</span>
          <span className={clsx(
            'text-sm font-black tabular-nums',
            riskScore >= 70 ? 'text-red-400' : riskScore >= 40 ? 'text-orange-400' : 'text-yellow-400',
          )}>{riskScore}</span>
        </div>
      </div>

      {/* Risk summary bar */}
      <div className="px-3 shrink-0">
        <div className="flex gap-1 text-[9px]">
          {(['critical', 'high'] as const).map((lvl) => {
            const chokeCount = SEMI_CHOKES.filter((c) => c.severity === lvl).length;
            const fabCount = SEMI_FABS.filter((f) => f.riskLevel === lvl).length;
            return (
              <div key={lvl} className={clsx('flex-1 rounded px-1.5 py-1 text-center', RISK_COLORS[lvl].bg, RISK_COLORS[lvl].border, 'border')}>
                <div className={clsx('font-black text-[10px]', RISK_COLORS[lvl].text)}>{chokeCount + fabCount}</div>
                <div className="text-slate-500 uppercase tracking-wide text-[7px]">{lvl}</div>
              </div>
            );
          })}
          <div className="flex-1 rounded px-1.5 py-1 text-center bg-slate-800/30 border border-slate-700/30">
            <div className="font-black text-[10px] text-slate-300">{SEMI_FABS.reduce((s, f) => s + f.globalSharePct, 0)}%</div>
            <div className="text-slate-500 uppercase tracking-wide text-[7px]">TW+KR share</div>
          </div>
          <div className="flex-1 rounded px-1.5 py-1 text-center bg-slate-800/30 border border-slate-700/30">
            <div className="font-black text-[10px] text-slate-300">{SEMI_EQUIPMENT.filter((e) => e.monopolyPct >= 80).length}</div>
            <div className="text-slate-500 uppercase tracking-wide text-[7px]">monopolies</div>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 px-3 shrink-0">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'flex-1 px-1 py-0.5 rounded text-[9px] font-medium transition-colors border',
              tab === t.id
                ? 'bg-cyan-900/50 text-cyan-300 border-cyan-800'
                : 'text-slate-500 border-slate-800 hover:text-slate-300',
            )}
          >
            {t.label}
            <span className="ml-0.5 text-[8px] opacity-60">({t.count})</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="overflow-y-auto flex-1 px-3 pb-2">
        {tab === 'chokes'    && SEMI_CHOKES.map((c) => <ChokeRow key={c.id} c={c} />)}
        {tab === 'fabs'      && SEMI_FABS.map((f) => <FabRow key={f.id} fab={f} />)}
        {tab === 'equipment' && SEMI_EQUIPMENT.map((e) => <EquipRow key={e.company + e.product} eq={e} />)}
        {tab === 'materials' && SEMI_MATERIALS.map((m) => <MaterialRow key={m.material} mat={m} />)}
        {tab === 'news' && (
          semiNews.length === 0
            ? <p className="text-[9px] text-slate-500 text-center py-6">No chip-related news yet — loading feeds…</p>
            : semiNews.map((n) => <NewsRow key={n.id} item={n} />)
        )}
      </div>
    </div>
  );
};
